import React, { useRef, useState } from 'react';
import { Upload, Download, AlertTriangle, CheckCircle, Image as ImageIcon } from 'lucide-react';
import emailApi from '../../services/emailApi';

interface Props {
  html: string;
  variables: Record<string, string>;
  useLayout: boolean;
  onUseLayoutChange: (v: boolean) => void;
  onChange: (html: string) => void;
}

/**
 * Traer un correo diseñado en otro editor (Mailchimp, Beefree, Canva, Figma…).
 *
 * El problema típico no es el HTML sino las imágenes: apuntan al editor de
 * origen, caducan o vienen incrustadas en base64 y Gmail las recorta. Al
 * importar, el servidor las descarga y las vuelve a alojar aquí, dejando el
 * correo listo para enviarse a cualquier bandeja.
 */
const ImportHtmlPanel: React.FC<Props> = ({ html, variables, useLayout, onUseLayoutChange, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ imported: number; skipped: string[]; message: string } | null>(null);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const remoteImages = (html.match(/(?:src\s*=\s*["']|url\(\s*["']?)(https?:\/\/|data:image)/gi) || []).length;
  const hosted = (html.match(/\/uploads\/email\//g) || []).length;
  const pendingImages = Math.max(0, remoteImages - hosted);

  const runImport = async () => {
    setError(''); setResult(null);
    if (!html.trim()) { setError('Primero pega el HTML del correo'); return; }
    try {
      setBusy(true);
      const r = await emailApi.importHtml(html);
      onChange(r.html);
      setResult({ imported: r.imported, skipped: r.skipped || [], message: r.message });
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo procesar el HTML');
    } finally {
      setBusy(false);
    }
  };

  const readFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result || ''));
    reader.readAsText(file);
  };

  return (
    <div className="imp">
      <p className="imp-help">
        Pega aquí el HTML completo del correo. Al importarlo, las imágenes que estén en otro
        servidor o incrustadas en base64 se descargan y quedan alojadas en la plataforma, que es
        lo que evita que lleguen rotas o recortadas.
      </p>

      <textarea
        className="sf-input imp-code"
        rows={12}
        value={html}
        onChange={e => onChange(e.target.value)}
        placeholder="<html>…</html>"
        onDrop={e => {
          const f = e.dataTransfer.files?.[0];
          if (f) { e.preventDefault(); readFile(f); }
        }}
      />

      <div className="imp-actions">
        <button type="button" className="btn-ghost" onClick={() => fileRef.current?.click()}>
          <Upload size={14} /> Cargar archivo .html
        </button>
        <button type="button" className="btn-ink" onClick={runImport} disabled={busy}>
          <Download size={14} /> {busy ? 'Procesando…' : 'Importar y alojar imágenes'}
        </button>
        {pendingImages > 0 && (
          <span className="imp-pending">
            <ImageIcon size={13} /> {pendingImages} imagen(es) todavía apuntan afuera
          </span>
        )}
      </div>

      <input ref={fileRef} type="file" accept=".html,text/html" hidden
        onChange={e => { const f = e.target.files?.[0]; if (f) readFile(f); e.target.value = ''; }} />

      {error && <div className="sf-empty">{error}</div>}

      {result && (
        <div className={`imp-result ${result.skipped.length ? 'imp-result--warn' : ''}`}>
          <div className="imp-result-head">
            {result.skipped.length ? <AlertTriangle size={14} /> : <CheckCircle size={14} />}
            {result.message}
          </div>
          {result.skipped.length > 0 && (
            <div className="imp-skipped">
              No se pudieron traer estas imágenes (revisa que sean públicas y súbelas a mano si hace falta):
              <ul>{result.skipped.map(u => <li key={u}>{u}</li>)}</ul>
            </div>
          )}
        </div>
      )}

      <label className="em-check">
        <input type="checkbox" checked={useLayout} onChange={e => onUseLayoutChange(e.target.checked)} />
        <span>Envolver con el diseño del programa (logo y pie). Desmárcalo si tu HTML ya trae su propio diseño.</span>
      </label>

      <div className="imp-vars">
        <span className="rte-vars-label">Puedes usar estas variables dentro del HTML:</span>
        {Object.keys(variables).map(token => (
          <code key={token} className="imp-var" title={variables[token]}>{token}</code>
        ))}
      </div>
    </div>
  );
};

export default ImportHtmlPanel;
